import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { TimerOutlined as TimerOutlinedIcon } from "../icons";
import { EventAvailable as EventAvailableIcon } from "../icons";
import { EventBusy as EventBusyIcon } from "../icons";
import { T } from "../routes/Translate";
import { getEventDates, getNextEventDate, formatEventDate } from "../routes/eventsUtils";

const pad = (n) => String(n).padStart(2, "0");
const formatCountdown = (ms) => {
  const total = Math.max(Math.floor(ms / 1000), 0);
  const days = Math.floor(total / 86400);
  const time = `${pad(Math.floor((total % 86400) / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
  return days > 0 ? `${days}d ${time}` : time;
};

// Cuenta regresiva hasta la próxima fecha del evento (ver getNextEventDate):
// "Empieza en 3d 04:12:05" -> "¡Es hoy!" el mismo día -> la siguiente función
// si tiene varias -> "Finalizado" cuando pasaron todas. Las etiquetas van en
// <T> y los números afuera, para no retraducir cada segundo.
const EventCountdown = ({ event }) => {
  const [now, setNow] = useState(() => new Date());
  const next = getNextEventDate(event, now);

  useEffect(() => {
    if (!next) return undefined;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [next ? next.getTime() : null]); // eslint-disable-line react-hooks/exhaustive-deps

  const dates = getEventDates(event);
  if (dates.length === 0) return null;

  const box = (color, icon, children) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color, my: 1, flexWrap: "wrap" }}>
      {icon}
      {children}
    </Box>
  );
  const label = (content) => (
    <Typography variant="body2" sx={{ fontWeight: 600, color: "inherit" }}>{content}</Typography>
  );

  if (!next) {
    return box("text.disabled", <EventBusyIcon sx={{ fontSize: 18 }} />, label(<T>Evento finalizado</T>));
  }

  const upcomingCount = dates.filter((d) => d >= next).length;
  const nextLabel = dates.length > 1 && (
    <Typography variant="caption" sx={{ color: "text.secondary", width: "100%" }}>
      <T>Próxima función:</T> {formatEventDate(next, { withWeekday: true })}
      {upcomingCount > 1 && <> · <T>quedan</T> {upcomingCount} <T>fechas</T></>}
    </Typography>
  );

  // Sin hora, la fecha empieza a las 00:00: ese día entero muestra "¡Es hoy!".
  // Con hora, desde que empieza hasta el final del día también.
  if (now >= next) {
    return box(
      "success.main",
      <EventAvailableIcon sx={{ fontSize: 18 }} />,
      <>
        {label(<T>¡Es hoy!</T>)}
        {nextLabel}
      </>
    );
  }

  const remaining = next - now;
  return box(
    remaining <= 24 * 60 * 60 * 1000 ? "error.main" : "warning.main",
    <TimerOutlinedIcon sx={{ fontSize: 18 }} />,
    <>
      {label(<T>Empieza en</T>)}
      <Typography
        variant="body2"
        sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: "0.95rem", color: "inherit" }}
      >
        {formatCountdown(remaining)}
      </Typography>
      {nextLabel}
    </>
  );
};

export default EventCountdown;
